import { useEffect, useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  ScatterController,
  Filler,
  CategoryScale,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
  ScatterController,
  Filler
);

const isFiniteNum = (v) => Number.isFinite(Number(v));

function clamp(v, a, b) {
  return Math.min(Math.max(v, a), b);
}

/**
 * PitchPlotAudio (UX-pass):
 * - ✅ DISABLE time_stamped completely: no words, no vertical lines, no word lane
 * - ✅ Unvoiced strategy: treat <=0 / non-finite as null so pitch line breaks
 * - ✅ View window: [first voiced, last voiced]
 *   - cursor “waits” before first voiced and “sticks” after last voiced
 * - ✅ Remove local maxima red line/dots
 * - ✅ Hide x-axis labels/title (cleaner if no words)
 */
export default function PitchPlotAudio({
  intensity,
  time_stamped, // ignored on purpose
  currentTime = 0,
}) {
  const [showDerivative, setShowDerivative] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const xs = useMemo(() => intensity?.pitch?.xs || [], [intensity]);
  const ysRaw = useMemo(() => intensity?.pitch?.ys || [], [intensity]);
  const dYsOriginal = useMemo(
    () => intensity?.pitch_1st_derivative?.ys || [],
    [intensity]
  );

  // Mobile detection (no words now)
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // ✅ Unvoiced -> null (breaks line)
  const ys = useMemo(() => {
    const out = new Array(ysRaw.length);
    for (let i = 0; i < ysRaw.length; i++) {
      const v = Number(ysRaw[i]);
      out[i] = isFiniteNum(v) && v > 0 ? v : null;
    }
    return out;
  }, [ysRaw]);

  // ✅ first/last voiced indices -> x-window
  const { voicedXMin, voicedXMax } = useMemo(() => {
    if (!xs.length || !ys.length) return { voicedXMin: 0, voicedXMax: 1 };

    let first = -1;
    let last = -1;

    for (let i = 0; i < ys.length; i++) {
      if (ys[i] != null) {
        first = i;
        break;
      }
    }
    for (let i = ys.length - 1; i >= 0; i--) {
      if (ys[i] != null) {
        last = i;
        break;
      }
    }

    // No voiced at all => show whole range
    if (first === -1 || last === -1) {
      return { voicedXMin: xs[0] ?? 0, voicedXMax: xs[xs.length - 1] ?? 1 };
    }

    return { voicedXMin: xs[first], voicedXMax: xs[last] };
  }, [xs, ys]);

  // ✅ y-range from voiced only (avoid negatives/unvoiced)
  const { yMin, yMax } = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    for (let i = 0; i < ys.length; i++) {
      const v = ys[i];
      if (v == null) continue;
      min = Math.min(min, v);
      max = Math.max(max, v);
    }
    if (!Number.isFinite(min) || !Number.isFinite(max)) return { yMin: 0, yMax: 1 };
    return { yMin: min, yMax: max };
  }, [ys]);

  const yRange = useMemo(() => Math.max(1e-6, yMax - yMin), [yMax, yMin]);

  // ✅ cursor clamps to voiced window (wait/stick behavior)
  const cursorX = useMemo(() => {
    if (!xs.length) return null;
    const t = Number(currentTime);
    const raw = isFiniteNum(t) ? t : voicedXMin;
    return clamp(raw, voicedXMin, voicedXMax);
  }, [currentTime, xs.length, voicedXMin, voicedXMax]);

  // 🟣 Derivative scaling (optional)
  const dYsScaled = useMemo(() => {
    if (dYsOriginal.length !== xs.length || !xs.length) return [];
    const absMax =
      Math.max(...dYsOriginal.map((v) => Math.abs(Number(v) || 0))) || 1;

    // Use the visible y window for scaling
    const scaleFactor = (yRange * 0.5) / absMax;
    const verticalShift = yMin + yRange * 0.25;

    const out = new Array(dYsOriginal.length);
    for (let i = 0; i < dYsOriginal.length; i++) {
      const v = Number(dYsOriginal[i]);
      out[i] = isFiniteNum(v) ? v * scaleFactor + verticalShift : null;
    }
    return out;
  }, [dYsOriginal, xs.length, yMin, yRange, xs]);

  const data = useMemo(() => {
    const datasets = [];

    // 🔵 Main Pitch line (gapped)
    datasets.push({
      label: "Pitch (Hz)",
      data: xs.map((x, i) => ({ x, y: ys[i] })),
      borderColor: "#2563eb",
      borderWidth: isMobile ? 1.75 : 2.25,
      tension: isMobile ? 0.25 : 0.42,
      cubicInterpolationMode: "monotone",
      pointRadius: 0,
      showLine: true,
      spanGaps: false,
      datalabels: { display: false },
    });

    // 🟣 Optional derivative (scaled)
    if (showDerivative && dYsScaled.length === xs.length) {
      datasets.push({
        label: "Pitch Derivative (scaled)",
        data: xs.map((x, i) => ({ x, y: dYsScaled[i] })),
        borderColor: "purple",
        borderDash: [4, 4],
        borderWidth: isMobile ? 1 : 2,
        tension: isMobile ? 0.25 : 0.42,
        pointRadius: 0,
        showLine: true,
        spanGaps: true,
        datalabels: { display: false },
      });
    }

    // 🟢 Playback cursor
    if (cursorX != null) {
      datasets.push({
        label: "Playback cursor",
        data: [
          { x: cursorX, y: 0 }, // y-axis starts at 0
          { x: cursorX, y: yMax + yRange * 0.1 },
        ],
        borderColor: "rgba(16,185,129,1)",
        borderWidth: isMobile ? 1.6 : 1.8,
        pointRadius: 0,
        showLine: true,
        datalabels: { display: false },
      });
    }

    return { datasets };
  }, [xs, ys, isMobile, showDerivative, dYsScaled, cursorX, yMax, yRange]);

  const options = useMemo(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      layout: { padding: { left: 8, right: 12, top: 8, bottom: 8 } },
      scales: {
        x: {
          type: "linear",
          min: voicedXMin, // ✅ trim to voiced region
          max: voicedXMax,
          grid: { drawBorder: false, color: "rgba(0,0,0,0.05)" },
          ticks: { display: false }, // ✅ no x labels/title
          title: { display: false },
        },
        y: {
  min: 0,
  max: yMax + yRange * 0.1,
  grid: { color: "rgba(0,0,0,0.05)", drawBorder: false },
  title: {
    display: true,
    text: "Pitch (Hz)",
    font: { size: isMobile ? 12 : 14 },
  },
  ticks: {
    font: { size: isMobile ? 10 : 12 },
    maxTicksLimit: 5,
    callback: (value) => Number(value).toFixed(1), // ✅ ONE DECIMAL
  },
},
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: !isMobile,
          mode: "nearest",
          intersect: false,
        },
        datalabels: { display: false },
      },
      interaction: {
        mode: "nearest",
        axis: "x",
        intersect: false,
      },
    };
  }, [isMobile, voicedXMin, voicedXMax, yMax, yRange]);

  // ✅ Render decision AFTER hooks
  const hasData = xs.length > 0;
  if (!hasData) return null;

  return (
    <div className="flex flex-col gap-2 w-full min-w-0">
      {/* ✅ Toggle for derivative */}
      <label className={`flex items-center gap-2 select-none ${isMobile ? "text-xs" : "text-sm"}`}>
        <input
          type="checkbox"
          checked={showDerivative}
          onChange={() => setShowDerivative((v) => !v)}
          className={isMobile ? "scale-90" : ""}
        />
        Show Pitch 1st Derivative (scaled)
      </label>

      <div
        className={`w-full min-w-0 overflow-x-auto bg-white rounded-lg border border-gray-200 p-2 ${
          isMobile ? "h-48" : "h-64"
        }`}
      >
        <Line data={data} options={options} />
      </div>
    </div>
  );
}